import { WidgetType } from "@/lib/form-v2";

/**
 * Layer-native field registry for OTC form v3.
 *
 * This is intentionally independent of the v2 `buildFieldMeta` /
 * `otcSchemaConfig` machinery. v2 metadata bundles full-contract visibility,
 * derive, and effect logic that assumes a complete create/update payload.
 * Layered v3 instead edits a backend-`owned_fields`-scoped subset of fields and
 * must also be able to render fields that are NOT `trading_contract` columns at
 * all (e.g. `exit_date`, which is layer/event metadata).
 *
 * The registry only describes how to LABEL and RENDER a field. It carries no
 * visibility or business rules: editability is gated by backend `owned_fields`,
 * and contract-type/commodity relevance is a secondary display concern handled
 * elsewhere.
 *
 * Select fields whose options come from caller-provided lists
 * (customers/traders/accounts) or from the field's own current value
 * (option-month combos) declare an `optionsSource`. The renderer resolves the
 * actual option list from that source; the registry never holds the data.
 */
export type OtcLayerFieldOptionsSource =
  | "contract_type"
  | "commodity"
  | "customer"
  | "trader"
  | "otc_account"
  | "hta_account"
  | "option_month"
  | "rolled_option_month"
  | "rolled_basis_month"
  | "option_month_etc_one";

export interface OtcLayerFieldDef {
  label: string;
  widget: WidgetType;
  /** Where a select widget sources its options from. Omitted for non-selects. */
  optionsSource?: OtcLayerFieldOptionsSource;
  /** Step hint for numeric inputs, matching v2 conventions. */
  step?: string;
  /**
   * Layer/event metadata field that is NOT a `trading_contract` column.
   * These are invisible to v2 (not in `OtcFormValues`/schemas) and must be
   * explicitly supported by v3. Example: `exit_date`.
   */
  isLayerMetadata?: boolean;
}

/**
 * Labels mirror the v2 field meta so the UI reads consistently across forms.
 * Widgets also mirror v2 (`text` | `float` | `int` | `select` | `date` |
 * `checkbox`).
 */
export const OTC_LAYER_FIELD_REGISTRY: Record<string, OtcLayerFieldDef> = {
  // ========================== Core ==========================
  date: { label: "Date", widget: "date" },
  end_date: { label: "Expiration Date", widget: "date" },
  contract_type: { label: "Contract Type", widget: "select", optionsSource: "contract_type" },
  implied_pairing_contract_type: {
    label: "Implied Pairing Type",
    widget: "select",
    optionsSource: "contract_type",
  },
  customer_id: { label: "Customer", widget: "select", optionsSource: "customer" },
  commodity: { label: "Commodity", widget: "select", optionsSource: "commodity" },

  // ========================== Option / Trade ==========================
  option_month: { label: "OTC Opt. Month", widget: "select", optionsSource: "option_month" },
  quantity: { label: "Quantity", widget: "float", step: "5000" },
  trade_reference: { label: "Trade Reference", widget: "text" },
  account_no_a: { label: "Brokerage OTC", widget: "select", optionsSource: "otc_account" },

  // ========================== Steps ==========================
  step_one: { label: "Step 1", widget: "float" },
  step_two: { label: "Step 2", widget: "float" },
  step_three: { label: "Step 3", widget: "float" },
  double_up: { label: "Double Up", widget: "float" },

  // ========================== Canola futures (inputs only) ==========================
  implied_fx_amount: { label: "Implied FX Amount", widget: "float", step: "0.0001" },
  actual_contract_futures: { label: "Quoted Step 3 Futures", widget: "float" },
  actual_contract_futures_step_one: { label: "Quoted Step 1 Futures", widget: "float" },
  actual_contract_futures_step_two: { label: "Quoted Step 2 Futures", widget: "float" },
  actual_contract_futures_double_up: { label: "Quoted Double Up Futures", widget: "float" },
  final_futures_price: { label: "Final Quoted", widget: "float" },
  final_futures_expected_price: { label: "Final Priced Amount", widget: "float" },
  final_futures_price_date: { label: "Final Price Date", widget: "date" },

  // ========================== Premium Offer WDU ==========================
  account_no_b: { label: "Brokerage HTA", widget: "select", optionsSource: "hta_account" },
  futures_price: { label: "Futures Price", widget: "float" },
  otc_futures: { label: "OTC Futures", widget: "float" },

  // ========================== Financials ==========================
  option_premium: { label: "Option Premium", widget: "float" },
  contract_fee: { label: "Initial Premium + Fee", widget: "float" },
  contract_no: { label: "Contract #", widget: "text" },
  contract_reference: { label: "Contract Reference", widget: "text" },

  // ========================== Adjustment slot 1 ==========================
  adj_option_premium: { label: "Adj Option Premium", widget: "float" },
  adj_contract_fee: { label: "Adj Initial Premium + Fee", widget: "float" },
  last_adjusted_date: { label: "Adjusted Date", widget: "date" },

  // ========================== Adjustment slot 2 ==========================
  adj_option_premium_two: { label: "Adj Option Premium 2", widget: "float" },
  adj_contract_fee_two: { label: "Adj Initial Premium + Fee 2", widget: "float" },
  adjusted_date_two: { label: "Adjusted Date 2", widget: "date" },

  // ========================== Adjustment slot 3 ==========================
  adj_option_premium_three: { label: "Adj Option Premium 3", widget: "float" },
  adj_contract_fee_three: { label: "Adj Initial Premium + Fee 3", widget: "float" },
  adjusted_date_three: { label: "Adjusted Date 3", widget: "date" },

  // ========================== Adjustment slot 4 ==========================
  adj_option_premium_four: { label: "Adj Option Premium 4", widget: "float" },
  adj_contract_fee_four: { label: "Adj Initial Premium + Fee 4", widget: "float" },
  adjusted_date_four: { label: "Adjusted Date 4", widget: "date" },

  // ========================== Adjustment slot 5 ==========================
  adj_option_premium_five: { label: "Adj Option Premium 5", widget: "float" },
  adj_contract_fee_five: { label: "Adj Initial Premium + Fee 5", widget: "float" },
  adjusted_date_five: { label: "Adjusted Date 5", widget: "date" },

  // ========================== Accumulator ==========================
  start_date: { label: "Start Date", widget: "date" },
  weeks: { label: "Weeks", widget: "float" },
  period_qty: { label: "Period Qty", widget: "float" },
  knockout: { label: "Knockout", widget: "float" },

  // ========================== Shipment / Trader ==========================
  shipment_start_date: { label: "Shipment Start Date", widget: "date" },
  shipment_end_date: { label: "Shipment End Date", widget: "date" },
  trader_id: { label: "Trader", widget: "select", optionsSource: "trader" },
  amendment_trader_id: { label: "Amendment Trader", widget: "select", optionsSource: "trader" },
  crop_year: { label: "Crop Year", widget: "int", step: "1" },

  // ========================== Roll / Carry / Basis ==========================
  rolled_option_month: { label: "Futures Rolled Month", widget: "select", optionsSource: "rolled_option_month" },
  carry: { label: "Futures Carry", widget: "float" },
  rolled_basis_month: { label: "Basis Rolled Month", widget: "select", optionsSource: "rolled_basis_month" },
  basis_carry: { label: "Basis Carry", widget: "float" },

  // ========================== HTA DU ==========================
  option_month_etc_one: { label: "HTA DU Opt. Month", widget: "select", optionsSource: "option_month_etc_one" },

  // ========================== Notes / Flags ==========================
  notes: { label: "Notes", widget: "text" },
  has_invoice_fees: { label: "Invoice Fees", widget: "checkbox" },
  has_contract_adjustment: { label: "Contract Fee Add/Ded", widget: "checkbox" },

  // ========================== Layer / event metadata (not contract columns) ==========================
  exit_date: { label: "Exit Date", widget: "date", isLayerMetadata: true },
  exit_quantity: { label: "Exit Quantity", widget: "float", isLayerMetadata: true },
  exit_final_futures_price: {
    label: "Exit Final Futures Price",
    widget: "float",
    isLayerMetadata: true,
  },
};

/** Convenience lookup; returns undefined for unknown/derived fields. */
export function getOtcLayerFieldDef(field_name: string): OtcLayerFieldDef | undefined {
  return OTC_LAYER_FIELD_REGISTRY[field_name];
}
